import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AzilisSwitch3, { AzilisSwitch } from "../../components/Switch";
import PlusIcon from "../../components/SideTableIcons/PlusIcon";
import {
  LovableAddRowButton,
  LovableSwitchWrap,
  LovableRowActions,
} from "../NewProductCategories/ProductDetailsLCA/components/LovableFlowControls";
import SaveIcon from "../../components/SideTableIcons/SaveIcon";
import MailIcon from "../../components/SideTableIcons/MailIcon";
import EditableResultDropdownAutocomplete from "../../components/EditableResultDropdownAutocomplete";
import EditableAutocompleteNonCategory from "../../components/EditableAutocompleteNonCategory";
import EditableResultDropdownSubgroups from "../../components/EditableDropdownSubgroups";
import MultipleCustomColumn from "./components/MultipleCustomColumn";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useUserAccess } from "../../components/WriteAccessContext";
import BuyerInput from "../../components/BuyerInput/BuyerInput";
import ApiAutocompleteDropdown from "../../components/ApiAutocompleteDropdown";
import EditableMultiSelectAutocomplete from "../../components/EditableMultiSelectDropdown";
import OpenCircIndicator from "./components/OpenCircIndicator";
import { useTableVariant } from "./TableVariantContext";
import { booleanToSwitchWhich } from "./switchDisplayValue";

const AddNewItemRow = ({
  columns,
  onAdd,
  onAddRow,
  customInputs,
  dropdownData,
  hiddenColumns,
  handleValidation,
  disableRules,
  noShow,
  dynamicPlaceholders,
  newRows,
  setOpen,
  onNewItemFormReset,
  autoShow = false,
}) => {
  const [newItemData, setNewItemData] = useState({});
  const { userAccess } = useUserAccess();
  const [naStates, setNaStates] = useState({});
  const [showNewRow, setShowNewRow] = useState(Boolean(autoShow));
  const isLovable = useTableVariant() === "lovable";

  useEffect(() => {
    if (autoShow) setShowNewRow(true);
  }, [autoShow]);

  useEffect(() => {
    const defaultValues = columns.reduce((acc, column) => {
      if (column.type === "switch") {
        acc[column.accessor] = column.choice1 || "Yes";
      }
      return acc;
    }, {});

    setNewItemData((prev) => ({ ...prev, ...defaultValues }));
  }, [columns]);

  const handleInputChange = (columnId, value) => {
    setNewItemData((prev) => {
      const updatedData = { ...prev, [columnId]: value };

      const placeholderConfig =
        dynamicPlaceholders && dynamicPlaceholders[columnId];
      if (placeholderConfig) {
        const { targetField, getPlaceholder } = placeholderConfig;
        const selectedPrimitive =
          value && typeof value === "object"
            ? value.value ?? value.code ?? value.name
            : value;
        const placeholderValue = getPlaceholder(
          selectedPrimitive,
          dropdownData[columnId],
          updatedData,
          "new",
          value
        );
        if (placeholderValue !== undefined) {
          updatedData[targetField] = placeholderValue;
        }
      }

      return updatedData;
    });
  };

  const handleNaClick = (columnId) => {
    setNaStates((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));

    setNewItemData((prev) => ({
      ...prev,
      [columnId]: prev[columnId] && !naStates[columnId] ? prev[columnId] : "",
    }));
  };

  const resetFormData = () => {
    // Reset to default values for switches, empty for others
    const defaultValues = columns.reduce((acc, column) => {
      if (column.type === "switch") {
        acc[column.accessor] = column.choice1 || "Yes";
      } else {
        acc[column.accessor] = "";
      }
      return acc;
    }, {});

    setNewItemData(defaultValues);
    setNaStates({});
    onNewItemFormReset?.();
  };

  const handleAdd = () => {
    if (handleValidation(newItemData)) {
      onAdd(newItemData);
      resetFormData();
      setShowNewRow(false);
    }
  };

  const handleAddNewRow = () => {
    setShowNewRow(!showNewRow);
    if (showNewRow) {
      resetFormData();
    }
  };

  const renderInput = (column) => {
    if (hiddenColumns.includes(column.accessor)) {
      return null;
    }
    const isDisabled =
      (disableRules && disableRules[column.accessor]
        ? disableRules[column.accessor](newItemData)
        : false) || naStates[column.accessor];

    const isNotShown =
      noShow && noShow[column.accessor]
        ? noShow[column.accessor](newItemData)
        : false;

    const inputStyle = {
      maxWidth: "100%",
      whiteSpace: "nowrap",
      textAlign: "center",
      opacity: isDisabled ? 0.5 : 1,
      pointerEvents: isDisabled ? "none" : "auto",
    };

    const nonDropdownInputStyle = {
      ...inputStyle,
      height: "28px",
    };

    const dropdownInputStyle = {
      ...inputStyle,
      display: "flex",
      alignItems: "center",
    };

    const newRowUnitLabel = (col) => {
      if (!col.unit) return null;
      const raw =
        typeof col.unit === "function"
          ? col.unit("new", newItemData)
          : col.unit;
      if (raw == null || raw === "") return null;
      const normalized =
        raw === "xx"
          ? "unit"
          : typeof raw === "string"
          ? raw.replaceAll("/xx", "/unit")
          : raw;
      return (
        <span
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: "#666",
            whiteSpace: "nowrap",
            textAlign: "center",
            width: "100%",
          }}
        >
          {normalized}
        </span>
      );
    };

    const wrapNewRowDropdownWithUnit = (col, inner) =>
      col.unit ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            width: "100%",
          }}
        >
          <div style={{ width: "100%" }}>{inner}</div>
          {newRowUnitLabel(col)}
        </div>
      ) : (
        inner
      );

    const naBtnStyle = {
      marginLeft: "5px",
      color: "white",
      opacity: naStates[column.accessor] ? 1 : 0.5,
      cursor: "pointer",
      padding: "3px",
    };

    switch (column.type) {
      case "text":
      case "number":
        const isHidden =
          column.isNA &&
          column.isNA === true &&
          Object.keys(naStates).length > 0 &&
          Object.keys(naStates)?.[0] === column.accessorNA &&
          Object.values(naStates)?.[0] === true
            ? true
            : column.isNA === false &&
              Object.keys(naStates).length > 0 &&
              Object.keys(naStates)?.[0] === column.accessorNA &&
              Object.values(naStates)?.[0] === false
            ? true
            : false;
        return (
          !isHidden && (
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
              }}
            >
              {" "}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <input
                  type={column.type}
                  value={newItemData[column.accessor] || ""}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (column.type === "number") {
                      // Allow empty string or valid decimal inputs
                      if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                        // Convert to number only when needed for validation
                        const numValue = value === "" ? "" : Number(value);

                        if (value !== "" && !isNaN(numValue)) {
                          // Apply min/max constraints after conversion
                          if (column.minValue !== undefined) {
                            value = Math.max(
                              column.minValue,
                              numValue
                            ).toString();
                          }
                          if (column.maxValue !== undefined) {
                            value = Math.min(
                              column.maxValue,
                              numValue
                            ).toString();
                          }
                        }

                        handleInputChange(column.accessor, value);
                      }
                    } else {
                      handleInputChange(column.accessor, value);
                    }
                  }}
                  placeholder={column.placeholder}
                  style={{
                    ...nonDropdownInputStyle,
                    width: column.isNABtn ? "calc(100% - 5px)" : column.width,
                    marginRight: column.isNABtn ? "5px" : "0",
                    maxWidth: "100%",
                  }}
                  disabled={isDisabled}
                />
                <>
                  {column.unit && typeof column.unit === "function" ? (
                    <span
                      style={{
                        marginTop: "4px",
                        color: "#666",
                        fontSize: "12px",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {column.unit("new", newItemData)}
                    </span>
                  ) : column.unit ? (
                    <span
                      style={{
                        marginTop: "4px",
                        color: "#666",
                        fontSize: "12px",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {column.unit}
                    </span>
                  ) : null}
                </>
              </div>
              <>
                {column.isNABtn && (
                  <span
                    className="na-btn-input"
                    style={naBtnStyle}
                    onClick={() => handleNaClick(column.accessor)}
                  >
                    n/a
                  </span>
                )}
              </>
            </div>
          )
        );
      case "dropdown":
        const options = dropdownData[column.accessor] || [];
        if (isNotShown === true) return;
        if (column.dropdownComponent) {
          const CustomDropdown = column.dropdownComponent;
          return wrapNewRowDropdownWithUnit(
            column,
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CustomDropdown
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  ...content,
                  title: content?.name ?? content?.title ?? content?.value,
                  value: content?.value,
                  name: content?.name ?? content?.title,
                  category: content?.category,
                }))}
                disabled={isDisabled}
              />
            </div>
          );
        }
        if (column.isMultiSelect) {
          return wrapNewRowDropdownWithUnit(
            column,
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableMultiSelectAutocomplete
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  ...content,
                  title: content?.name ?? content?.title ?? content?.value,
                  value: content?.value,
                  name: content?.name ?? content?.title,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else if (column.isSubgroup) {
          return wrapNewRowDropdownWithUnit(
            column,
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableResultDropdownSubgroups
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  ...content,
                  subgroup: content.subgroup,
                  subgroup_en: content.subgroup_en,
                  title: content?.name ?? content?.title ?? content?.value,
                  value: content?.value,
                  name: content?.name ?? content?.title,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else if (column.isCategory) {
          return wrapNewRowDropdownWithUnit(
            column,
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableResultDropdownAutocomplete
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  ...content,
                  category: content.category,
                  title: content?.name ?? content?.title ?? content?.value,
                  value: content?.value,
                  name: content?.name ?? content?.title,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else {
          return wrapNewRowDropdownWithUnit(
            column,
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableAutocompleteNonCategory
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  ...content,
                  title: content?.name ?? content?.title ?? content?.value,
                  value: content?.value,
                  name: content?.name ?? content?.title,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        }
      case "apiAutocomplete":
        return (
          <div
            style={{
              ...dropdownInputStyle,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ApiAutocompleteDropdown
              chosen={newItemData[column.accessor]}
              setValue={(value) => handleInputChange(column.accessor, value)}
              disabled={isDisabled}
              edit={true}
            />
          </div>
        );
      case "switch": {
        const switchWhich = booleanToSwitchWhich(
          newItemData[column.accessor],
          column
        );
        return (
          <LovableSwitchWrap which={switchWhich} column={column}>
            <AzilisSwitch
              key={hiddenColumns.join(",")}
              choice1={column.choice1 || "Yes"}
              choice2={column.choice2 || "No"}
              which={switchWhich}
              setWhich={(value) => handleInputChange(column.accessor, value)}
              styleNo={column.styleNo || 6}
              disabled={isDisabled}
            />
          </LovableSwitchWrap>
        );
      }
      case "switch3": {
        const switchWhich = booleanToSwitchWhich(
          newItemData[column.accessor],
          column
        );
        return (
          <LovableSwitchWrap which={switchWhich} column={column}>
            <AzilisSwitch3
              key={hiddenColumns.join(",")}
              choice1={column.choice1 || "Yes"}
              choice2={column.choice2 || "No"}
              choice3={column.choice3}
              value1={column.choice1 || "Yes"}
              value2={column.choice2 || "No"}
              value3={column.choice3}
              which={switchWhich}
              setWhich={(value) => handleInputChange(column.accessor, value)}
              styleNo={column.styleNo || 6}
              disabled={isDisabled}
            />
          </LovableSwitchWrap>
        );
      }
      case "custom":
        const CustomInput = customInputs?.[column.accessor];
        if (!CustomInput) {
          // If no custom input is provided, render a simple text input as fallback
          return (
            <input
              type="text"
              value={newItemData[column.accessor] || ""}
              onChange={(e) => handleInputChange(column.accessor, e.target.value)}
              style={nonDropdownInputStyle}
              placeholder={column.placeholder || ""}
            />
          );
        }
        return (
          <CustomInput
            value={newItemData[column.accessor]}
            onChange={(value) => handleInputChange(column.accessor, value)}
            style={nonDropdownInputStyle}
          />
        );
      case "multipleCustom":
        return (
          <MultipleCustomColumn
            customInputs={column.customInputs}
            renderInput={(customInput) =>
              renderInput({
                ...customInput,
                accessor: `${column.accessor}_${customInput.accessor}`,
              })
            }
            layout={column.layout}
            naStates={naStates}
          />
        );
      case "buyerInput":
        return (
          <BuyerInput
            buyerOptions={column.buyerOptions}
            value={newItemData[column.accessor]}
            onChange={(value) => handleInputChange(column.accessor, value)}
            disabled={isDisabled}
          />
        );
      case "indicator":
        // OpenCirc indicator type - for new rows, show empty state
        return (
          <OpenCircIndicator
            component={null}
            onClick={column.onIndicatorClick}
          />
        );
      case "checkbox":
        return (
          <FormControlLabel
            control={<Checkbox color="success" />}
            checked={newItemData[column.accessor] || false}
            onChange={(e) =>
              handleInputChange(column.accessor, e.target.checked)
            }
            sx={{ marginRight: 0, marginLeft: "-3px" }}
            disabled={isDisabled}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showNewRow && (
        <tr className={isLovable ? "lovable-new-item-row" : undefined}>
          {columns.map((column) => (
            <td
              key={column.accessor}
              style={{
                width:
                  column.type === "spacer"
                    ? column.width
                    : hiddenColumns.includes(column.accessor)
                    ? "30px"
                    : "auto",
                padding:
                  column.type === "spacer"
                    ? "0"
                    : hiddenColumns.includes(column.accessor)
                    ? "0 5px"
                    : "8px",
              }}
              className={[
                column.type === "spacer" ? "spacer-column" : "td-with-background",
                isLovable ? "lovable-table-cell" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {column.type !== "spacer" && renderInput(column)}
            </td>
          ))}
          <td className={isLovable ? "lovable-table-cell" : undefined}>
            {userAccess === "write" &&
              (isLovable ? (
                <LovableRowActions
                  onSave={handleAdd}
                  onMail={() => setOpen(true)}
                  showDelete={false}
                  showCompare={false}
                />
              ) : (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <SaveIcon
                    style={{ cursor: "pointer" }}
                    onClick={handleAdd}
                  />
                  <MailIcon
                    style={{ width: "15px", cursor: "pointer" }}
                    onClick={() => setOpen(true)}
                  />
                </div>
              ))}
          </td>
        </tr>
      )}
      {!newRows && (
        <tr className={isLovable ? "lovable-add-row-bar" : "bg-add-new-row"}>
          <td colSpan={columns.length + 1}>
            {isLovable ? (
              <LovableAddRowButton onClick={handleAddNewRow} />
            ) : (
              <PlusIcon style={{ cursor: "pointer" }} onClick={handleAddNewRow} />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

AddNewItemRow.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func.isRequired,
  customInputs: PropTypes.objectOf(PropTypes.func),
  dropdownData: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default AddNewItemRow;
